import React, { useState, useMemo, useCallback, memo, useRef, useEffect } from 'react';
import { Checkbox, Button, Tooltip, Tag, InputNumber } from 'antd';
import { LinkOutlined, HolderOutlined } from '@ant-design/icons';
import { PropertyData } from '@/services/scraper.service';

interface AmostraTableProps {
  properties: PropertyData[];
  selectedProperties: Set<string>;
  onPropertyToggle: (property: PropertyData) => void;
  onOrderChange: (orderedProperties: PropertyData[]) => void;
  onPropertyUpdate?: (url: string, field: string, value: number | null) => void;
  disabled?: boolean;
}

// Constantes
const CUB_PADRAO = 2500;

// Funcoes de calculo (fora do componente para evitar recriacao)
const calcularVU = (valor: number | null | undefined, area: number | null | undefined) => {
  if (!valor || !area || area === 0) return null;
  return valor / area;
};

const calcularVUConstruido = (idade: number | null | undefined) => {
  if (idade === null || idade === undefined) return null;
  const depreciacao = Math.min(idade * 0.01, 0.5);
  return CUB_PADRAO * (1 - depreciacao);
};

const calcularVUTerreno = (
  valor: number | null | undefined,
  areaConstruida: number | null | undefined,
  areaTerreno: number | null | undefined,
  idade: number | null | undefined
) => {
  if (!valor || areaConstruida === null || areaConstruida === undefined || !areaTerreno || areaTerreno === 0 || idade === null || idade === undefined) return null;
  const depreciacao = Math.min(idade * 0.01, 0.5);
  const cubAjustado = CUB_PADRAO * (1 - depreciacao);
  const valorConstrucao = cubAjustado * areaConstruida;
  return (valor - valorConstrucao) / areaTerreno;
};

// Cache de formatacao de moeda
const currencyCache = new Map<number, string>();
const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '-';
  const cached = currencyCache.get(value);
  if (cached) return cached;
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  currencyCache.set(value, formatted);
  return formatted;
};

// Mapa de logos
const logoMap: Record<string, string> = {
  vivareal: '/assets/vivareal.png',
  zapimoveis: '/assets/zap.png',
  chavesnamao: '/assets/chavesnamao.png',
  imovelweb: '/assets/imovelweb.png',
};

// Celula editavel otimizada
const EditableCell = memo(({
  value,
  url,
  field,
  onUpdate,
  disabled,
  suffix,
  isCurrency
}: {
  value: number | null | undefined;
  url: string;
  field: string;
  onUpdate: (url: string, field: string, value: number | null) => void;
  disabled: boolean;
  suffix?: string;
  isCurrency?: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sincronizar tempValue quando value muda externamente
  useEffect(() => {
    if (!editing) setTempValue(value);
  }, [value, editing]);

  const handleSave = useCallback(() => {
    setEditing(false);
    if (tempValue !== value) {
      onUpdate(url, field, tempValue ?? null);
    }
  }, [tempValue, value, onUpdate, url, field]);

  const handleClick = useCallback(() => {
    if (!disabled) setEditing(true);
  }, [disabled]);

  const handleChange = useCallback((v: number | null) => {
    setTempValue(v);
  }, []);

  if (editing && !disabled) {
    return (
      <InputNumber
        ref={inputRef}
        autoFocus
        size="small"
        value={tempValue}
        onChange={handleChange}
        onBlur={handleSave}
        onPressEnter={handleSave}
        decimalSeparator=","
        style={{ width: 75 }}
      />
    );
  }

  const display = value != null
    ? isCurrency
      ? `R$ ${value.toLocaleString('pt-BR')}`
      : `${value.toLocaleString('pt-BR')}${suffix || ''}`
    : '-';

  return (
    <span
      onClick={handleClick}
      className={`editable-cell ${disabled ? '' : 'editable'} ${isCurrency ? 'currency' : ''} ${value == null ? 'empty' : ''}`}
    >
      {display}
    </span>
  );
});

// Componente de linha otimizado
interface RowProps {
  property: PropertyData;
  index: number;
  isSelected: boolean;
  isDragging: boolean;
  isOver: boolean;
  disabled: boolean;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnter: (index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onToggle: () => void;
  onUpdate: (url: string, field: string, value: number | null) => void;
}

const AmostraRow = memo(({
  property,
  index,
  isSelected,
  isDragging,
  isOver,
  disabled,
  onDragStart,
  onDragOver,
  onDragEnter,
  onDrop,
  onDragEnd,
  onToggle,
  onUpdate,
}: RowProps) => {
  const logo = logoMap[property.fonte?.toLowerCase()];
  const vu = useMemo(() => calcularVU(property.valor_venda_num, property.metragem_num), [property.valor_venda_num, property.metragem_num]);
  const vuConstruido = useMemo(() => calcularVUConstruido(property.idade), [property.idade]);
  const vuTerreno = useMemo(() => calcularVUTerreno(property.valor_venda_num, property.metragem_num, property.terreno, property.idade),
    [property.valor_venda_num, property.metragem_num, property.terreno, property.idade]);

  const handleDragStart = useCallback((e: React.DragEvent) => onDragStart(e, index), [onDragStart, index]);
  const handleDragEnter = useCallback(() => onDragEnter(index), [onDragEnter, index]);
  const handleDrop = useCallback((e: React.DragEvent) => onDrop(e, index), [onDrop, index]);

  const className = useMemo(() =>
    `amostra-row ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isOver && !isDragging ? 'over' : ''}`,
    [isSelected, isDragging, isOver]
  );

  return (
    <div
      className={className}
      draggable
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDragEnter={handleDragEnter}
      onDrop={handleDrop}
      onDragEnd={onDragEnd}
    >
      <div className="col-drag"><HolderOutlined /></div>
      <div className="col-check">
        <Checkbox checked={isSelected} onChange={onToggle} disabled={disabled} />
      </div>
      <div className="col-rank">
        <span className={isSelected ? 'rank-selected' : ''}>{index + 1}</span>
      </div>
      <div className="col-info">
        {logo ? <img src={logo} alt="" className="logo" /> : <Tag className="fonte-tag">{property.fonte}</Tag>}
        <Tooltip title={property.titulo}><div className="titulo">{property.titulo}</div></Tooltip>
        <div className="location">{property.bairro}, {property.cidade}</div>
        <div className="details">
          <span className="detail-editable">
            <EditableCell value={property.quartos_num} url={property.url} field="quartos_num" onUpdate={onUpdate} disabled={disabled} suffix=" qto" />
          </span>
          <span className="detail-editable">
            <EditableCell value={property.banheiros_num} url={property.url} field="banheiros_num" onUpdate={onUpdate} disabled={disabled} suffix=" ban" />
          </span>
          <span className="detail-editable">
            <EditableCell value={property.vagas_num} url={property.url} field="vagas_num" onUpdate={onUpdate} disabled={disabled} suffix=" vag" />
          </span>
        </div>
      </div>
      <div className="col-tipo">
        {property.tipo_imovel ? (
          <Tag color={
            property.tipo_imovel.toLowerCase().includes('apartamento') ? 'blue' :
            property.tipo_imovel.toLowerCase().includes('casa') ? 'orange' :
            property.tipo_imovel.toLowerCase().includes('terreno') ? 'brown' :
            property.tipo_imovel.toLowerCase().includes('comercial') ? 'cyan' : 'default'
          } style={{ fontSize: 10, margin: 0 }}>
            {property.tipo_imovel}
          </Tag>
        ) : <span style={{ color: '#ccc', fontSize: 10 }}>-</span>}
      </div>
      <div className="col-link">
        <Button type="link" icon={<LinkOutlined />} href={property.url} target="_blank" size="small" />
      </div>
      <div className="col-valor">
        <EditableCell value={property.valor_venda_num} url={property.url} field="valor_venda_num" onUpdate={onUpdate} disabled={disabled} isCurrency />
      </div>
      <div className="col-area">
        <EditableCell value={property.metragem_num} url={property.url} field="metragem_num" onUpdate={onUpdate} disabled={disabled} suffix=" m²" />
      </div>
      <div className="col-terreno">
        <EditableCell value={property.terreno} url={property.url} field="terreno" onUpdate={onUpdate} disabled={disabled} suffix=" m²" />
      </div>
      <div className="col-idade">
        <EditableCell value={property.idade} url={property.url} field="idade" onUpdate={onUpdate} disabled={disabled} suffix=" a" />
      </div>
      <div className="col-vu">{formatCurrency(vu)}</div>
      <div className={`col-vuc ${vuConstruido ? 'has-value' : ''}`}>
        {vuConstruido ? formatCurrency(vuConstruido) : '-'}
      </div>
      <div className={`col-vut ${vuTerreno !== null ? (vuTerreno < 0 ? 'negative' : 'positive') : ''}`}>
        {vuTerreno !== null ? formatCurrency(vuTerreno) : '-'}
      </div>
    </div>
  );
});

// Componente principal
function AmostraTableComponent({
  properties,
  selectedProperties,
  onPropertyToggle,
  onOrderChange,
  onPropertyUpdate,
  disabled = false,
}: AmostraTableProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const draggedRef = useRef<PropertyData | null>(null);
  const overIndexRef = useRef<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    draggedRef.current = properties[index];
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Esconde a imagem fantasma do navegador
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(img, 0, 0);
  }, [properties]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    if (overIndexRef.current !== index) {
      overIndexRef.current = index;
      setOverIndex(index);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = dragIndex;
    if (sourceIndex !== null && sourceIndex !== targetIndex && draggedRef.current) {
      const newData = [...properties];
      newData.splice(sourceIndex, 1);
      newData.splice(targetIndex, 0, draggedRef.current);
      onOrderChange(newData);
    }
  }, [dragIndex, properties, onOrderChange]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
    overIndexRef.current = null;
    draggedRef.current = null;
  }, []);

  const handleUpdate = useCallback((url: string, field: string, value: number | null) => {
    onPropertyUpdate?.(url, field, value);
  }, [onPropertyUpdate]);

  // Pre-calcular callbacks de toggle para evitar recriacao
  const toggleCallbacks = useMemo(() => {
    return properties.map((p) => () => onPropertyToggle(p));
  }, [properties, onPropertyToggle]);

  return (
    <div className="amostra-table-v3">
      <div className="helper-text">
        <HolderOutlined style={{ marginRight: 6 }} />
        Arraste para reordenar | Clique nos valores para editar
      </div>

      {/* Header */}
      <div className="amostra-header">
        <div className="col-drag"></div>
        <div className="col-check"></div>
        <div className="col-rank">#</div>
        <div className="col-info">Amostra</div>
        <div className="col-tipo">Tipo</div>
        <div className="col-link"></div>
        <div className="col-valor">Valor</div>
        <div className="col-area">Área</div>
        <div className="col-terreno">Terreno</div>
        <div className="col-idade">Idade</div>
        <div className="col-vu">VU</div>
        <div className="col-vuc">VU Constr.</div>
        <div className="col-vut">VU Terreno</div>
      </div>

      {/* Body */}
      <div className="amostra-body">
        {properties.map((property, index) => (
          <AmostraRow
            key={property.url}
            property={property}
            index={index}
            isSelected={selectedProperties.has(property.url)}
            isDragging={dragIndex === index}
            isOver={overIndex === index}
            disabled={disabled}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onToggle={toggleCallbacks[index]}
            onUpdate={handleUpdate}
          />
        ))}
      </div>


      <style>{`
        .amostra-table-v3 {
          font-size: 12px;
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
          contain: layout style;
        }
        .amostra-table-v3 .helper-text {
          padding: 6px 12px;
          background: #fafafa;
          color: #888;
          font-size: 11px;
          border-bottom: 1px solid #e8e8e8;
        }
        .amostra-table-v3 .amostra-header {
          display: flex;
          align-items: center;
          background: #fafafa;
          border-bottom: 1px solid #e8e8e8;
          font-weight: 600;
          color: #666;
          padding: 8px 0;
          font-size: 11px;
        }
        .amostra-table-v3 .amostra-body {
          max-height: 450px;
          overflow-y: auto;
          will-change: scroll-position;
        }
        .amostra-table-v3 .amostra-row {
          display: flex;
          align-items: center;
          border-bottom: 1px solid #f0f0f0;
          padding: 12px 0;
          min-height: 72px;
          cursor: grab;
          background: #fff;
          position: relative;
          contain: layout style;
        }
        .amostra-table-v3 .amostra-row:active { cursor: grabbing; }
        .amostra-table-v3 .amostra-row.selected { background: #f6ffed; }
        .amostra-table-v3 .amostra-row.dragging {
          background: #f0f7ff !important;
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 0 0 2px #1890ff;
          transform: scale(1.01);
          z-index: 10;
        }
        .amostra-table-v3 .amostra-row.dragging .col-drag { color: #1890ff; }
        .amostra-table-v3 .amostra-row.over {
          background: linear-gradient(to bottom, #e6f7ff 0%, #fff 100%) !important;
        }
        .amostra-table-v3 .amostra-row.over::before {
          content: '';
          position: absolute;
          top: 0;
          left: 8px;
          right: 8px;
          height: 2px;
          background: #1890ff;
          border-radius: 2px;
        }
        .amostra-table-v3 .col-drag { width: 30px; text-align: center; color: #bbb; }
        .amostra-table-v3 .col-check { width: 32px; }
        .amostra-table-v3 .col-rank { width: 28px; font-weight: 600; text-align: center; }
        .amostra-table-v3 .col-rank .rank-selected { color: #52c41a; }
        .amostra-table-v3 .col-info { flex: 1; min-width: 200px; padding-right: 12px; }
        .amostra-table-v3 .col-info .logo { height: 14px; margin-bottom: 4px; }
        .amostra-table-v3 .col-info .fonte-tag { font-size: 9px; padding: 0 4px; margin: 0 0 4px 0; }
        .amostra-table-v3 .col-info .titulo {
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 200px;
          font-size: 13px;
          color: #333;
          margin-bottom: 2px;
        }
        .amostra-table-v3 .col-info .location { font-size: 11px; color: #666; margin-bottom: 4px; }
        .amostra-table-v3 .col-info .details { display: flex; gap: 6px; flex-wrap: wrap; }
        .amostra-table-v3 .col-info .details .detail-editable {
          font-size: 10px;
          color: #666;
          background: #f0f0f0;
          padding: 2px 6px;
          border-radius: 4px;
          cursor: pointer;
        }
        .amostra-table-v3 .col-info .details .detail-editable:hover { background: #e0e0e0; }
        .amostra-table-v3 .col-info .details .detail-editable .editable-cell { color: #666; }
        .amostra-table-v3 .col-tipo { width: 95px; text-align: center; display: flex; align-items: center; justify-content: center; }
        .amostra-table-v3 .col-link { width: 36px; }
        .amostra-table-v3 .col-valor { width: 100px; font-size: 13px; }
        .amostra-table-v3 .col-area { width: 70px; font-size: 12px; }
        .amostra-table-v3 .col-terreno { width: 80px; font-size: 12px; }
        .amostra-table-v3 .col-idade { width: 60px; font-size: 12px; }
        .amostra-table-v3 .col-vu { width: 90px; font-weight: 600; font-size: 12px; }
        .amostra-table-v3 .col-vuc { width: 90px; font-weight: 600; font-size: 12px; color: #ccc; }
        .amostra-table-v3 .col-vuc.has-value { color: #faad14; }
        .amostra-table-v3 .col-vut { width: 90px; font-weight: 600; font-size: 12px; color: #ccc; }
        .amostra-table-v3 .col-vut.positive { color: #52c41a; }
        .amostra-table-v3 .col-vut.negative { color: #ff4d4f; }
        .amostra-table-v3 .editable-cell { cursor: default; color: #999; }
        .amostra-table-v3 .editable-cell.editable { cursor: pointer; }
        .amostra-table-v3 .editable-cell.editable:not(.empty) { color: #333; }
        .amostra-table-v3 .editable-cell.currency { color: #1890ff; font-weight: 600; }
        .amostra-table-v3 .editable-cell.editable:hover {
          background: #f5f5f5;
          border-radius: 3px;
          padding: 1px 3px;
          margin: -1px -3px;
        }
      `}</style>
    </div>
  );
}

export const AmostraTable = memo(AmostraTableComponent);
